import { useEffect, useRef } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { SCORECARD_CARD, SCORECARD_ROW_DIVIDER } from "../../constants";
import { formatScorecardScore, getVerdictDotClassName } from "../../utils";
import ScorecardRerunIconButton from "../../ScorecardRerunIconButton";
import ScorecardScoreDeltaBadge from "../Detail/ScorecardScoreDeltaBadge";
import SmartTableStalenessIndicator from "../../../../../SmartTableStalenessIndicator";

import ScorecardEmptyState from "./ScorecardEmptyState";
import ScorecardCopyableError from "./ScorecardCopyableError";

import { cn } from "@/lib/utils";

const ScorecardRowResultMeta = ({ item }: { item: ScorecardRowResultItem }) => {
  if (item.errorMessage) {
    return <ScorecardCopyableError message={item.errorMessage} compact />;
  }
  if (item.evidence) {
    return (
      <div className="line-clamp-2 break-words text-xs leading-5 text-muted-foreground">
        {item.evidence}
      </div>
    );
  }
  return null;
};

const ScorecardRowResultDot = ({ item }: { item: ScorecardRowResultItem }) => (
  <span
    className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", getVerdictDotClassName(item.verdict))}
  />
);

export interface ScorecardRowResultItem {
  rowIndex: number;
  label: string;
  verdict: string;
  score: number | null;
  drift?: number | null;
  evidence?: string | null;
  errorMessage?: string;
  isStale?: boolean;
}

interface ScorecardRowResultsListProps {
  items: ScorecardRowResultItem[];
  onOpenRow: (rowIndex: number) => void;
  onRerunRow?: (rowIndex: number) => void;
  getIsRowRerunning?: (rowIndex: number) => boolean;
  isRerunDisabled?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadingMessage?: string;
  emptyMessage: string;
}

/**
 * Shared row list used by both the all-evaluators and per-evaluator row
 * browsing views, so the density, typography, and empty/loading states stay
 * identical regardless of which scope the user is filtering by.
 */
const ScorecardRowResultsList = ({
  items,
  onOpenRow,
  onRerunRow,
  getIsRowRerunning,
  isRerunDisabled = false,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  loadingMessage = "Loading rows…",
  emptyMessage,
}: ScorecardRowResultsListProps) => {
  const scrollParentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 68,
    overscan: 8,
  });

  // Infinite scroll: fetch the next page automatically once the user scrolls
  // within a few rows of the bottom of the loaded set.
  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  useEffect(() => {
    if (!hasMore || isLoadingMore || !onLoadMore) {
      return;
    }
    if (lastVisibleIndex >= items.length - 5) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, items.length, lastVisibleIndex, onLoadMore]);

  if (isLoading && items.length === 0) {
    return (
      <div className="rounded-lg border border-border/70 bg-background px-3 py-4 text-center text-sm text-muted-foreground">
        {loadingMessage}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <ScorecardEmptyState
        className="border-border/70 px-4 py-8"
        description={emptyMessage}
        descriptionClassName="text-sm text-muted-foreground"
      />
    );
  }

  return (
    <div className={SCORECARD_CARD}>
      <div ref={scrollParentRef} className="max-h-[520px] overflow-auto">
        <div className="relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const item = items[virtualRow.index];
            if (!item) {
              return null;
            }
            return (
              <div
                key={item.rowIndex}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                className={cn(SCORECARD_ROW_DIVIDER, "absolute left-0 top-0 w-full")}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenRow(item.rowIndex)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenRow(item.rowIndex);
                    }
                  }}
                  className="group flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/15 focus:outline-none focus-visible:bg-muted/20 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring/40"
                >
                  <ScorecardRowResultDot item={item} />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <div
                        className="line-clamp-2 min-w-0 break-words text-sm leading-5 text-foreground"
                        title={item.label}
                      >
                        {item.label}
                      </div>
                      {item.isStale ? (
                        <SmartTableStalenessIndicator
                          className="shrink-0"
                          tooltipContent="Stale — re-run to refresh"
                        />
                      ) : null}
                    </div>
                    <ScorecardRowResultMeta item={item} />
                  </div>
                  <div className="flex shrink-0 items-center gap-1 pt-0.5">
                    <span className="text-sm font-semibold tabular-nums text-foreground">
                      {formatScorecardScore(item.score)}
                    </span>
                    <ScorecardScoreDeltaBadge drift={item.drift} />
                    {onRerunRow ? (
                      <ScorecardRerunIconButton
                        label={item.label}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          onRerunRow(item.rowIndex);
                        }}
                        isRerunning={Boolean(getIsRowRerunning?.(item.rowIndex))}
                        isDisabled={isRerunDisabled}
                      />
                    ) : null}
                    <ChevronRight
                      className="h-4 w-4 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {isLoadingMore ? (
        <div className="flex items-center justify-center gap-1.5 border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          Loading more rows…
        </div>
      ) : null}
    </div>
  );
};

export default ScorecardRowResultsList;
